import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import { FiArrowLeft, FiSearch } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { FEATURE_GROUP_OF, FEATURE_GROUPS, FeatureGroupId, FeatureId } from '../core/client/features.gen'
import { useFeatureFlags } from '../hooks/features/useFeatureFlags'
import { useLanguage } from '../hooks/utility/useLanguage'
import { FeatureAdminItem } from '../types/featureTypes'

// Dynamic per-client features (`knowledge_source.<key>`, see
// app/core/features/sources.py) are not in the catalog; they get their own
// section after the catalog groups.
const SOURCES_GROUP = 'knowledge_sources'
const KNOWLEDGE_SOURCE_PREFIX = 'knowledge_source.'

type SectionId = FeatureGroupId | typeof SOURCES_GROUP

const SECTION_ORDER: SectionId[] = [...(Object.keys(FEATURE_GROUPS) as FeatureGroupId[]), SOURCES_GROUP]

function sectionOf(item: FeatureAdminItem): SectionId {
  if (item.id.startsWith(KNOWLEDGE_SOURCE_PREFIX)) return SOURCES_GROUP
  return FEATURE_GROUP_OF[item.id as FeatureId] ?? 'admin'
}

function matches(item: FeatureAdminItem, needle: string): boolean {
  if (!needle) return true
  const haystack = `${item.name} ${item.id} ${item.description} ${item.depends_on.join(' ')}`.toLowerCase()
  return haystack.includes(needle)
}

/**
 * Reshaped-admin console for runtime feature flags.
 *
 * Rows are segmented by the catalog groups (features.yaml → features.gen.ts)
 * plus a section for per-source flags, with a text filter and group chips on
 * top. Data and the toggle write live in `useFeatureFlags`.
 */
const FeatureFlagsPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { features, version, isLoading, toggle, isToggling } = useFeatureFlags()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const subtleColor = useColorModeValue('gray.500', 'gray.400')
  const containerBg = useColorModeValue('gray.50', 'gray.900')
  const sectionHeaderBg = useColorModeValue('gray.50', 'gray.700')

  const [query, setQuery] = useState('')
  const [activeSection, setActiveSection] = useState<SectionId | null>(null)

  const sectionLabel = (section: SectionId): string =>
    section === SOURCES_GROUP
      ? t('features.groupKnowledgeSources', { defaultValue: 'Knowledge sources' })
      : FEATURE_GROUPS[section]

  // Section → rows, catalog order for sections, alphabetical within a section.
  const sections = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const bySection = new Map<SectionId, FeatureAdminItem[]>()
    for (const item of features) {
      const section = sectionOf(item)
      if (activeSection && section !== activeSection) continue
      if (!matches(item, needle)) continue
      const bucket = bySection.get(section) ?? []
      bucket.push(item)
      bySection.set(section, bucket)
    }
    return SECTION_ORDER.filter(section => bySection.has(section)).map(section => ({
      id: section,
      items: [...(bySection.get(section) ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    }))
  }, [features, query, activeSection])

  // Chips only for sections that actually have rows in this deployment.
  const availableSections = useMemo(() => {
    const present = new Set(features.map(sectionOf))
    return SECTION_ORDER.filter(section => present.has(section))
  }, [features])

  const totalShown = sections.reduce((sum, section) => sum + section.items.length, 0)

  return (
    <Box bg={containerBg} minH='100dvh' py={{ base: 4, md: 8 }} overflowY='auto'>
      <Container maxW='container.lg'>
        <VStack spacing={6} align='stretch'>
          <Flex align='center' gap={3}>
            <IconButton
              aria-label={t('common.back')}
              icon={<FiArrowLeft />}
              variant='ghost'
              onClick={() => navigate(-1)}
            />
            <Heading size='lg' flex='1'>
              {t('features.title', { defaultValue: 'Feature flags' })}
            </Heading>
            {version !== undefined && (
              <Text fontSize='sm' color={subtleColor}>
                {t('features.version', { version, defaultValue: `Version ${version}` })}
              </Text>
            )}
          </Flex>

          <Text color={subtleColor} fontSize='sm'>
            {t('features.intro', {
              defaultValue:
                'Switching a feature reloads every open client within seconds. Turning a feature off always works; turning one on requires its dependencies to be on.',
            })}
          </Text>

          {/* Filter bar */}
          <Flex gap={3} direction={{ base: 'column', md: 'row' }} align={{ md: 'center' }}>
            <InputGroup maxW={{ md: '320px' }}>
              <InputLeftElement pointerEvents='none' color={subtleColor}>
                <FiSearch />
              </InputLeftElement>
              <Input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder={t('features.filterPlaceholder', { defaultValue: 'Filter features…' })}
                bg={cardBg}
              />
            </InputGroup>
            <HStack spacing={2} flexWrap='wrap'>
              <Button
                size='sm'
                variant={activeSection === null ? 'solid' : 'outline'}
                onClick={() => setActiveSection(null)}
              >
                {t('features.groupAll', { defaultValue: 'All' })}
              </Button>
              {availableSections.map(section => (
                <Button
                  key={section}
                  size='sm'
                  variant={activeSection === section ? 'solid' : 'outline'}
                  onClick={() => setActiveSection(activeSection === section ? null : section)}
                >
                  {sectionLabel(section)}
                </Button>
              ))}
            </HStack>
            {!isLoading && (
              <Text fontSize='xs' color={subtleColor} ml={{ md: 'auto' }} whiteSpace='nowrap'>
                {t('features.shownCount', {
                  shown: totalShown,
                  total: features.length,
                  defaultValue: `${totalShown} of ${features.length}`,
                })}
              </Text>
            )}
          </Flex>

          <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' overflowX='auto'>
            {isLoading ? (
              <Flex justify='center' p={10}>
                <Spinner />
              </Flex>
            ) : sections.length === 0 ? (
              <Text p={8} textAlign='center' color={subtleColor}>
                {t('features.noMatches', { defaultValue: 'No features match this filter.' })}
              </Text>
            ) : (
              <Table size='sm' variant='simple'>
                <Thead>
                  <Tr>
                    <Th>{t('features.columnFeature', { defaultValue: 'Feature' })}</Th>
                    <Th>{t('features.columnStatus', { defaultValue: 'Stored' })}</Th>
                    <Th>{t('features.columnEffective', { defaultValue: 'Effective' })}</Th>
                    <Th>{t('features.columnDependsOn', { defaultValue: 'Depends on' })}</Th>
                    <Th isNumeric>{t('features.columnVersion', { defaultValue: 'Version' })}</Th>
                    <Th>{t('features.columnLastModified', { defaultValue: 'Last modified' })}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sections.map(section => (
                    <React.Fragment key={section.id}>
                      <Tr bg={sectionHeaderBg}>
                        <Td colSpan={6} py={2}>
                          <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' color={subtleColor}>
                            {sectionLabel(section.id)}
                          </Text>
                        </Td>
                      </Tr>
                      {section.items.map(item => {
                        const isOn = item.status === 'on'
                        return (
                          <Tr key={item.id}>
                            <Td>
                              <Text fontWeight='medium'>{item.name}</Text>
                              <Text fontSize='xs' color={subtleColor} fontFamily='mono'>
                                {item.id}
                              </Text>
                              {item.description && (
                                <Text fontSize='xs' color={subtleColor} mt={1}>
                                  {item.description}
                                </Text>
                              )}
                            </Td>
                            <Td>
                              <Switch
                                isChecked={isOn}
                                isDisabled={isToggling}
                                colorScheme='brand'
                                aria-label={`${item.name}: ${item.status}`}
                                onChange={() => toggle(item, isOn ? 'off' : 'on')}
                              />
                            </Td>
                            <Td>
                              {item.effective ? (
                                <Badge colorScheme='green'>{t('features.on', { defaultValue: 'on' })}</Badge>
                              ) : item.blockers.length > 0 ? (
                                <Tooltip
                                  label={t('features.blockedBy', {
                                    blockers: item.blockers.join(', '),
                                    defaultValue: `Blocked by: ${item.blockers.join(', ')}`,
                                  })}
                                >
                                  <Badge colorScheme='orange'>
                                    {t('features.blocked', { defaultValue: 'blocked' })}
                                  </Badge>
                                </Tooltip>
                              ) : (
                                <Badge>{t('features.off', { defaultValue: 'off' })}</Badge>
                              )}
                            </Td>
                            <Td>
                              <Text fontSize='xs' fontFamily='mono' color={subtleColor}>
                                {item.depends_on.length > 0 ? item.depends_on.join(', ') : '—'}
                              </Text>
                            </Td>
                            <Td isNumeric>{item.version}</Td>
                            <Td>
                              <Text fontSize='xs' color={subtleColor}>
                                {item.last_modified ? new Date(item.last_modified).toLocaleString() : '—'}
                              </Text>
                              {item.last_modified_by_email && (
                                <Text fontSize='xs' color={subtleColor}>
                                  {item.last_modified_by_email}
                                </Text>
                              )}
                            </Td>
                          </Tr>
                        )
                      })}
                    </React.Fragment>
                  ))}
                </Tbody>
              </Table>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}

export default FeatureFlagsPage
